import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import SnowLayer from "../components/SnowLayer";

const GUESTS = [2, 4, 6, 8, 10, 15, 20];
const STYLES = ["economica", "tradicional", "especial", "premium"];
const GOALS = ["deco", "ceia", "both", "all"];

export default function Onboarding() {
  const { t, lang } = useI18n();
  const { user, setUser } = useAuth();
  const config = useConfig();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({ country: user?.country || "BR", guests: 8, budget: null, style: "tradicional", goals: "all" });

  const symbol = symbolForCurrency(config, config?.countries.find((c) => c.code === ans.country)?.currency || "BRL");
  const currency = config?.countries.find((c) => c.code === ans.country)?.currency || "BRL";
  const tiers = config?.budget_tiers?.ceia?.[currency] || [];

  const finish = async () => {
    try {
      const { data } = await api.post("/onboarding", {
        country: ans.country, guests: ans.guests, budget: ans.budget, style: ans.style, goals: [ans.goals],
      });
      setUser({ ...user, ...data });
    } catch { /* ignore */ }
    navigate("/app", { replace: true });
  };

  const steps = [
    {
      q: t("onboarding.q_country"),
      body: (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {(config?.countries || []).map((c) => (
            <button key={c.code} data-testid={`onboarding-country-${c.code}`} onClick={() => setAns({ ...ans, country: c.code })}
              className={`rounded-xl border p-4 text-center transition ${ans.country === c.code ? "border-xmas-gold bg-xmas-gold/10" : "border-xmas-gold/20 hover:border-xmas-gold/50"}`}>
              <span className="text-2xl">{c.flag}</span>
              <p className="mt-1 text-sm text-xmas-snow">{c.name?.[lang] || c.code}</p>
            </button>
          ))}
        </div>
      ),
    },
    {
      q: t("onboarding.q_guests"),
      body: (
        <div className="flex flex-wrap justify-center gap-3">
          {GUESTS.map((g) => (
            <button key={g} onClick={() => setAns({ ...ans, guests: g })}
              className={`h-14 w-14 rounded-full text-lg font-semibold transition ${ans.guests === g ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>{g}</button>
          ))}
        </div>
      ),
    },
    {
      q: t("onboarding.q_budget"),
      body: (
        <div className="flex flex-wrap justify-center gap-3">
          {tiers.map((v) => (
            <button key={v} onClick={() => setAns({ ...ans, budget: v })}
              className={`rounded-full px-5 py-3 text-sm transition ${ans.budget === v ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>
              {t("deco.up_to")} {formatMoney(symbol, v)}
            </button>
          ))}
        </div>
      ),
    },
    {
      q: t("onboarding.q_style"),
      body: (
        <div className="grid grid-cols-2 gap-3">
          {STYLES.map((s) => (
            <button key={s} onClick={() => setAns({ ...ans, style: s })}
              className={`rounded-xl border py-4 text-sm capitalize transition ${ans.style === s ? "border-xmas-gold bg-xmas-gold/10 text-xmas-gold" : "border-xmas-gold/20 text-xmas-snow hover:border-xmas-gold/50"}`}>{s}</button>
          ))}
        </div>
      ),
    },
    {
      q: t("onboarding.q_goals"),
      body: (
        <div className="grid grid-cols-2 gap-3">
          {GOALS.map((g) => (
            <button key={g} onClick={() => setAns({ ...ans, goals: g })}
              className={`rounded-xl border py-4 text-sm transition ${ans.goals === g ? "border-xmas-gold bg-xmas-gold/10 text-xmas-gold" : "border-xmas-gold/20 text-xmas-snow hover:border-xmas-gold/50"}`}>{t(`onboarding.goal.${g}`)}</button>
          ))}
        </div>
      ),
    },
  ];

  const cur = steps[step];

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-xmas-navy" data-testid="onboarding-page">
      <SnowLayer count={25} />
      <div className="relative z-10 w-full max-w-lg px-4">
        <div className="mb-6 flex items-center justify-center gap-2">
          {steps.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-xmas-gold" : i < step ? "w-4 bg-xmas-gold/60" : "w-4 bg-xmas-navysurface"}`} />
          ))}
        </div>
        <div className="glass rounded-3xl p-6 text-center sm:p-8">
          <Sparkles className="mx-auto mb-4 h-7 w-7 text-xmas-gold" />
          <p className="mb-1 text-xs uppercase tracking-widest text-xmas-slate">{t("onboarding.title")}</p>
          <h2 className="mb-8 font-display text-2xl text-xmas-snow">{cur.q}</h2>
          {cur.body}
          <div className="mt-9 flex items-center justify-between">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1 text-sm text-xmas-slate hover:text-xmas-snow"><ArrowLeft className="h-4 w-4" />{t("cta.back")}</button>
            ) : (
              <button onClick={finish} className="text-sm text-xmas-slate hover:text-xmas-snow">{t("onboarding.skip")}</button>
            )}
            {step < steps.length - 1 ? (
              <button onClick={() => setStep(step + 1)} data-testid="onboarding-next" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider">
                {t("cta.continue")} <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={finish} data-testid="onboarding-finish" className="btn-gold rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider">
                {t("onboarding.finish")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
